import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDataProvider, useNotify, useRecordContext, useRefresh } from 'ra-core';
import { useState } from 'react';

interface InvoicePaymentModalProps {
    open: boolean;
    onClose: () => void;
}

export const InvoicePaymentModal = ({ open, onClose }: InvoicePaymentModalProps) => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [reference, setReference] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!record || !amount) return;

        const paymentAmount = parseFloat(amount);
        if (isNaN(paymentAmount) || paymentAmount <= 0) {
            notify('Please enter a valid amount', { type: 'error' });
            return;
        }

        setLoading(true);
        try {
            const currentPaid = parseFloat(record.paid_amount || '0');
            const newPaid = currentPaid + paymentAmount;
            const total = parseFloat(record.total_amount || '1');
            
            const isFullyPaid = newPaid >= total - 0.01; // fuzzy math
            
            const recordStatusStr = String(record.status || '').toLowerCase();

            await dataProvider.update('customer_invoices', {
                id: record.id,
                data: {
                    paid_amount: newPaid,
                    status: isFullyPaid ? 'paid' : (recordStatusStr === 'draft' ? 'sent' : record.status),
                    payment_status: isFullyPaid ? 'paid' : 'partial',
                },
                previousData: record
            });
            
            notify('Payment recorded successfully', { type: 'success' });
            setAmount('');
            setReference('');
            refresh();
            onClose();
        } catch (error) {
            notify('Error recording payment', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    if (!record) return null;

    const remainingBalance = (parseFloat(record.total_amount || '0') - parseFloat(record.paid_amount || '0')).toFixed(2);

    return (
        <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
            <DialogContent className="sm:max-w-[425px]">
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle>Record Payment</DialogTitle>
                        <DialogDescription>
                            Remaining balance: INR {remainingBalance}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="amount">Payment Amount (INR)</Label>
                            <Input
                                id="amount"
                                type="number"
                                step="0.01"
                                max={remainingBalance}
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="date">Payment Date</Label>
                            <Input
                                id="date"
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="reference">Reference (Transaction ID, Check No)</Label>
                            <Input
                                id="reference"
                                value={reference}
                                onChange={(e) => setReference(e.target.value)}
                                placeholder="e.g. UTR-12345678"
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading ? 'Saving...' : 'Record Payment'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
